import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, MousePointerClick, Megaphone, Music2, Youtube, Code2,
  Palette, Mail, GitBranch, Bot, Workflow, Film,
  PenTool, Users, ShoppingBag, Rocket, ArrowRight,
} from "lucide-react";
import SERVICES from "@/data/servicesData";
import ServiceDetailsModal from "@/components/landing/ServiceDetailsModal";

const ICONS = {
  seo: Search,
  "google-ads": MousePointerClick,
  "meta-ads": Megaphone,
  tiktok: Music2,
  youtube: Youtube,
  web: Code2,
  branding: Palette,
  email: Mail,
  funnel: GitBranch,
  "ai-chatbots": Bot,
  crm: Workflow,
  video: Film,
  content: PenTool,
  influencer: Users,
  marketplace: ShoppingBag,
  saas: Rocket,
};

export default function Services() {
  const [active, setActive] = useState(null);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-14 md:py-32 overflow-hidden"
    >
      <div className="aurora-soft" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-14 gap-6">
          <div className="max-w-2xl">
            <div className="text-[10px] font-mono-acc uppercase tracking-[0.3em] text-cyan-400 mb-3">
              / Capability stack
            </div>
            <h2 className="font-clash text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
              One agency. <span className="text-gradient-neon">Every growth lever.</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-sm md:text-base">
            16 specialised services, one P&amp;L. We replace 4–6 agencies with a single
            elite pod aligned to your revenue. Click any service for deliverables,
            milestones and outcomes.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.key] || Search;
            return (
              <motion.button
                type="button"
                key={s.key}
                onClick={() => setActive(s)}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: (i % 4) * 0.05 }}
                className="group relative overflow-hidden rounded-2xl glass p-5 md:p-6 ring-glow hover:-translate-y-0.5 transition-all duration-500 text-left"
                data-testid={`service-${s.key}`}
              >
                <div className="absolute -top-12 -right-12 h-32 w-32 rounded-full bg-purple-500/0 group-hover:bg-purple-500/15 blur-2xl transition-all duration-700" />
                <div className="relative flex items-start justify-between mb-6">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-400/15 to-purple-500/15 border border-white/10 flex items-center justify-center">
                    <Icon size={18} className="text-cyan-300" />
                  </div>
                  <span className="font-mono-acc text-[10px] tracking-widest text-zinc-600">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div className="relative">
                  <h3 className="font-clash text-lg md:text-xl font-medium tracking-tight text-white">
                    {s.name}
                  </h3>
                  <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                    {s.tagline}
                  </p>
                  {s.proofPoints && s.proofPoints.length > 0 && (
                    <ul className="mt-4 space-y-1.5">
                      {s.proofPoints.slice(0, 3).map((p, idx) => (
                        <li
                          key={idx}
                          className="flex items-start gap-1.5 text-[11px] leading-snug text-zinc-300"
                          data-testid={`service-proof-${s.key}-${idx}`}
                        >
                          <span className="text-cyan-300 mt-[3px] shrink-0">▸</span>
                          <span className="font-mono-acc tracking-tight">{p}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="relative mt-6 flex items-center text-xs text-zinc-500 group-hover:text-cyan-300 transition-colors">
                  See full engagement details
                  <span className="ml-1 transition-transform group-hover:translate-x-1">→</span>
                </div>
              </motion.button>
            );
          })}
        </div>

        <div className="mt-10 flex flex-col items-center gap-4">
          <Link
            to="/services"
            data-testid="services-view-all"
            className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-500/20 to-purple-500/20 border border-white/10 px-5 py-3 text-sm font-medium text-white hover:from-cyan-500/30 hover:to-purple-500/30 transition-colors"
          >
            View all 16 services in detail
            <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </Link>
          <p className="text-center text-xs text-zinc-500 max-w-2xl mx-auto leading-relaxed">
            Or tap any tile above for a quick deep-dive — deliverables, 90-day milestones,
            and recent client engagements.
          </p>
        </div>
      </div>

      {active && <ServiceDetailsModal service={active} onClose={() => setActive(null)} />}
    </section>
  );
}
